import { cn, genJudgingSizeColor } from '@/lib/utils';
import { formatBalanceWithCommas, formatCryptoPrice, formatPercentage, formatTimeAgo, formatValueWithCompactNumber } from '@/lib/format';
import TokenAvatar from '@/widget/TokenAvatar';
import { Browser, BscIcon, SolIcon, Telegram, Twitter2X } from '@/widget/Icon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';
import { useTheme } from '@/components/ThemeProvider';
import tokenApiService from '@/request/token/token.api.service';
import { ITxsTrending } from '@/request/token/token.response.interface';
import { useTradeStore } from '@/page/trade/store';
import { useAppStore } from '@/store';
import { useBoomWallet } from '@boomeme/wallet-sdk';

type websiteType = 'twitter' | 'telegram' | 'website' | 'scan';

export default () => {
  const { t } = useTranslation();
  const { fwClass, lang } = useAppStore();
  const { tokenInfo, priceChg, tokenRealtimePrice } = useTradeStore();
  const { theme } = useTheme();
  const { chain } = useBoomWallet();

  const marketCap = tokenInfo?.address ? BigNumber(tokenInfo?.totalSupply).times(tokenInfo?.price).toString() : 0;

  const [priceChgList, setPriceChgList] = useState<ITxsTrending[]>([]);

  const [priceChgIndex, setPriceChgIndex] = useState(0);
  const currentData = priceChgList[priceChgIndex];

  const onJump = (type: websiteType) => {
    switch (type) {
      case 'scan':
        if (chain == 'solana') {
          window.open(`https://solscan.io/token/${tokenInfo?.address}`);
        } else {
          window.open(`https://bscscan.com/token/${tokenInfo?.address}`);
        }
        break;
      case 'telegram':
        window.open(tokenInfo?.social.telegram);
        break;
      case 'twitter':
        window.open(tokenInfo?.social.twitter);
        break;
      case 'website':
        window.open(tokenInfo?.social.website);
        break;
      default:
        break;
    }
  };

  const calculatePercentages = (deno: number, numerator: number) => ((numerator / deno) * 100).toFixed(2);

  const getTokenPriceChg = async () => {
    if (tokenInfo?.address) {
      const res = await tokenApiService.getTxsTrending({ tokenAddress: tokenInfo?.address });
      if (res.code == 0) {
        setPriceChgList(res.data.txsTrending);
      }
    }
  };

  useEffect(() => {
    getTokenPriceChg();
  }, [tokenInfo?.address, chain]);

  return (
    <div className="flex flex-col gap-y-[20px]">
      <div>
        <div className="mb-[10px] flex items-center gap-x-[12px]">
          <TokenAvatar address={tokenInfo?.address || ''} logo={tokenInfo?.logo || ''} className="w-[30px] h-[30px]" />
          <div>
            <div className="flex items-center">
              <span className={cn('text-[24px]', fwClass)}>{tokenInfo?.symbol}</span>
            </div>
            <p className="text-[12px] text-secondaryTextColor">{formatTimeAgo(tokenInfo?.createdAt, { style: 'shortAgo', lang })}</p>
          </div>
        </div>
        <div className="flex items-center">
          <span className={cn(genJudgingSizeColor(priceChg), fwClass)}>
            ${formatCryptoPrice(tokenRealtimePrice)}&nbsp;{`(${formatPercentage(priceChg, { scale: true })})`}
          </span>
        </div>
      </div>
      <div>
        <p className="mb-[12px]">{t('Trade.marketInfo')}</p>
        <div className="border-[1px] border-borderColor p-[12px] rounded-[8px] mb-[12px]">
          <div className="flex items-center justify-between text-[12px] py-[6px]">
            <span className="text-secondaryTextColor">{t('Trade.marketCap')}</span>
            <span>{formatValueWithCompactNumber(marketCap, true)}</span>
          </div>
          <div className="flex items-center justify-between text-[12px] py-[6px]">
            <span className="text-secondaryTextColor">{t('Trade.liquidity')}</span>
            <span>{formatValueWithCompactNumber(tokenInfo?.liquidity, true)}</span>
          </div>
          <div className="flex items-center justify-between text-[12px] py-[6px]">
            <span className="text-secondaryTextColor">{t('Trade.24hVolume')}</span>
            <span>{formatValueWithCompactNumber(tokenInfo?.volume, true)}</span>
          </div>
          <div className="flex items-center justify-between text-[12px] py-[6px]">
            <span className="text-secondaryTextColor">{t('Trade.holders')}</span>
            <span>{formatBalanceWithCommas(tokenInfo?.holders)}</span>
          </div>
          <div className="flex items-center justify-between text-[12px] py-[6px]">
            <span className="text-secondaryTextColor">Top 10</span>
            <span>{formatPercentage(tokenInfo?.top10Percent, { scale: true, showPositiveSign: false })}</span>
          </div>
          <div className="flex items-center justify-between text-[12px] py-[6px]">
            <span className="text-secondaryTextColor">{t('Trade.createdAt')}</span>
            <span>{formatTimeAgo(tokenInfo?.createdAt, { style: 'shortAgo', lang })}</span>
          </div>
        </div>
        <div className="border-[1px] border-borderColor rounded-[8px] mb-[12px] flex h-[48px]">
          {priceChgList.map((item, index) => {
            return (
              <div
                className={cn('flex-1 flex flex-col items-center justify-center', {
                  'bg-[rgba(244,244,245,0.8)] dark:bg-[rgba(39,39,42,0.8)] ': index == priceChgIndex,
                })}
                key={index}
                onClick={() => setPriceChgIndex(index)}
              >
                <p className="text-[12px] text-secondaryTextColor">{item.timeRange}</p>
                <p className={cn('text-[14px]', fwClass, genJudgingSizeColor(item.txsChange))}>
                  {formatPercentage(item.txsChange, { showPositiveSign: true, scale: true })}
                </p>
              </div>
            );
          })}
        </div>
        <div className="flex">
          <div className="border-r-[1px] border-borderColor w-[85px]">
            <div className="mb-[15px]">
              <p className="text-[12px] text-secondaryTextColor">TXNS</p>
              <p className="text-[14px]">{formatValueWithCompactNumber(currentData?.txData.txns)}</p>
            </div>
            <div className="mb-[15px]">
              <p className="text-[12px] text-secondaryTextColor">{t('Trade.volumes')}</p>
              <p className="text-[14px]">{formatValueWithCompactNumber(currentData?.txData.volumes)}</p>
            </div>
            <div className="">
              <p className="text-[12px] text-secondaryTextColor">{t('Trade.makers')}</p>
              <p className="text-[14px]">{formatValueWithCompactNumber(currentData?.txData.makers)}</p>
            </div>
          </div>
          <div className="w-full pl-[20px]">
            <div className="mb-[12px]">
              <div className="flex justify-between text-secondaryTextColor text-[12px]">
                <span>{t('Trade.buys')}</span>
                <span>{t('Trade.sells')}</span>
              </div>
              <div className="flex justify-between text-[14px]">
                <span>{formatValueWithCompactNumber(currentData?.txData.buys)}</span>
                <span>{formatValueWithCompactNumber(currentData?.txData.sells)}</span>
              </div>
              <div className="flex">
                <div
                  className="h-[4px] opacity-[0.4] bg-buyColor"
                  style={{ width: calculatePercentages(currentData?.txData.txns, currentData?.txData.buys) + '%' }}
                ></div>
                <div
                  className="h-[4px] opacity-[0.4] bg-sellColor"
                  style={{ width: calculatePercentages(currentData?.txData.txns, currentData?.txData.sells) + '%' }}
                ></div>
              </div>
            </div>
            <div className="mb-[12px]">
              <div className="flex justify-between text-secondaryTextColor text-[12px]">
                <span>{t('Trade.buyVol')}</span>
                <span>{t('Trade.sellVol')}</span>
              </div>
              <div className="flex justify-between text-[14px]">
                <span>{formatValueWithCompactNumber(currentData?.txData.buyVol, true)}</span>
                <span>{formatValueWithCompactNumber(currentData?.txData.sellVol, true)}</span>
              </div>
              <div className="flex">
                <div
                  className="h-[4px] opacity-[0.4] bg-buyColor"
                  style={{ width: calculatePercentages(currentData?.txData.volumes, currentData?.txData.buyVol) + '%' }}
                ></div>
                <div
                  className="h-[4px] opacity-[0.4] bg-sellColor"
                  style={{ width: calculatePercentages(currentData?.txData.volumes, currentData?.txData.sellVol) + '%' }}
                ></div>
              </div>
            </div>
            <div className="mb-[12px]">
              <div className="flex justify-between text-secondaryTextColor text-[12px]">
                <span>{t('Trade.buyers')}</span>
                <span>{t('Trade.sellers')}</span>
              </div>
              <div className="flex justify-between text-[14px]">
                <span>{formatValueWithCompactNumber(currentData?.txData.buyers)}</span>
                <span>{formatValueWithCompactNumber(currentData?.txData.sellers)}</span>
              </div>
              <div className="flex">
                <div
                  className="h-[4px] opacity-[0.4] bg-buyColor"
                  style={{ width: calculatePercentages(currentData?.txData.makers, currentData?.txData.buyers) + '%' }}
                ></div>
                <div
                  className="h-[4px] opacity-[0.4] bg-sellColor"
                  style={{ width: calculatePercentages(currentData?.txData.makers, currentData?.txData.sellers) + '%' }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <p className="mb-[12px]">Links</p>
        <div className="flex gap-x-[36px]">
          {tokenInfo?.social.twitter !== '' && <Twitter2X theme={theme} className="size-[18px]" onClick={() => onJump('twitter')} />}
          {tokenInfo?.social.telegram !== '' && <Telegram theme={theme} className="size-[18px]" onClick={() => onJump('telegram')} />}
          {tokenInfo?.social.website !== '' && <Browser theme={theme} className="size-[18px]" onClick={() => onJump('website')} />}
          {chain == 'solana' ? (
            <SolIcon theme={theme} className="size-[18px]" onClick={() => onJump('scan')} />
          ) : (
            <BscIcon theme={theme} className="size-[18px]" onClick={() => onJump('scan')} />
          )}
        </div>
      </div>
    </div>
  );
};
